import React, { useEffect, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { useAuth } from '@opendoor/auth-fe';
import { Box, Text } from '@opendoor/bricks-next';
import { Loader } from '@opendoor/bricks/core';
import { ClientError } from 'graphql-request';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { TransactionState } from '__generated__/athena';

import { withNavHeader } from 'cms/withNavHeader';

import { getAthenaClient } from 'components/api';
import { getBuyerDashboardLayout } from 'components/buyer/BuyerDashboardLayout';
import GlobalStyles from 'components/shared/GlobalStyles';

import { NextPageWithLayout } from './_app';

// Transaction states that indicate an offer is no longer active
const INACTIVE_STATES: TransactionState[] = [
  'CANCELED',
  'BUYER_WALKED',
  'REJECTED',
  'CONTRACT_FELL_THROUGH',
  'BACKUP_EXPIRED',
  'CLOSED',
  'UNKNOWN',
];

const BuyerPage: React.FC = () => {
  const { authentication } = useAuth();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authentication.state === 'authenticated') {
      fetchOffers();
    }
  }, [authentication.state]);

  const fetchOffers = async () => {
    try {
      const result = await getAthenaClient().BuyerOffersMinimal();

      const offers = result?.customerAccount?.buying?.offers;

      if (!offers || offers.length === 0) {
        // Redirect to home if no offers found
        router.replace('/');
        return;
      }

      // Find the first active offer
      const activeOffer = offers.find(
        (o) => !INACTIVE_STATES.includes(o.transactionState as TransactionState),
      );

      if (!activeOffer) {
        // Redirect to home if no active offers found
        router.replace('/');
        return;
      }

      // Redirect to the specific offer page (replace to avoid back button issues)
      router.replace(`/dashboard/offer/${activeOffer.id}`);
    } catch (e) {
      if (e instanceof ClientError) {
        const isAuthError = e.response.errors?.some(
          (err) => err.message.includes('UNAUTHENTICATED') || err.message.includes('UNAUTHORIZED'),
        );

        if (isAuthError) {
          setError('You are not authorized to view this page');
        } else {
          setError('Failed to load offer details');
        }

        Sentry.captureException(e, {
          extra: { graphqlErrors: e.response.errors },
        });
      } else {
        setError('An unexpected error occurred');
        Sentry.captureException(e);
      }
    }
  };

  // Show error state if there's an error
  if (error) {
    return (
      <>
        <GlobalStyles fontWeights={['regular', 'semibold', 'bold', 'medium']} />
        <Head>
          <title>My Dashboard | Opendoor</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <Box
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          paddingBottom="$6x"
          padding="$8x"
        >
          <Text tag="h1" typography="$headerMedium" marginBottom="$4x" color="$contentPrimary">
            {error}
          </Text>
          <Text tag="p" typography="$bodyMedium" color="$contentSecondary">
            Please contact support if you believe this is an error.
          </Text>
        </Box>
      </>
    );
  }

  // Show loading state while fetching offers and redirecting
  return (
    <>
      <GlobalStyles fontWeights={['regular', 'semibold', 'bold', 'medium']} />
      <Head>
        <title>My Dashboard | Opendoor</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <Box flexDirection="column" alignItems="center" paddingTop="$8x">
        <Loader />
      </Box>
    </>
  );
};

const BuyerPageWithLayout = BuyerPage as NextPageWithLayout & { theme?: string };
BuyerPageWithLayout.getLayout = getBuyerDashboardLayout;
BuyerPageWithLayout.theme = 'novo';

export const getServerSideProps = withNavHeader();

export default BuyerPageWithLayout;
